import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { storage } from '../utils/storage'
import { loadMapState, saveMapState } from '../utils/mapValidation'

const MapContext = createContext()

/**
 * MapProvider - Centralized map state management
 * Provides shared state for all map instances (HomePageMap, Map, EntityDetailMap)
 * 
 * @param {string} mode - 'general' | 'entity' - determines persistence key
 * @param {React.ReactNode} children
 */
export function MapProvider({ children, mode = 'general' }) {
  // Core map state (shared across all map instances)
  const [entities, setEntities] = useState([])
  const [userLocation, setUserLocation] = useState(null)
  const [showUserLocation, setShowUserLocation] = useState(() => 
    storage.get('showUserLocation', false)
  )
  const [radius, setRadius] = useState(50) // km
  const [searchLocation, setSearchLocation] = useState('')
  const [searchLocationName, setSearchLocationName] = useState('')
  
  // Map instance references (for multi-map scenarios)
  const [mapRef, setMapRef] = useState(null)
  const [markerRefs, setMarkerRefs] = useState({})
  
  // Selection state
  const [selectedEntityId, setSelectedEntityId] = useState(null)
  const [focusedEntityId, setFocusedEntityId] = useState(null)
  const [isolatedEntityId, setIsolatedEntityId] = useState(null)
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Persist state on changes (debounced via useEffect)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveMapState({
        entities,
        userLocation,
        radius,
        searchLocation,
        searchLocationName
      }, mode)
    }, 500) // Debounce saves
    
    return () => clearTimeout(timeoutId)
  }, [entities, userLocation, radius, searchLocation, searchLocationName, mode])
  
  // Restore state on mount
  useEffect(() => {
    const persistedState = loadMapState(mode)
    if (persistedState) {
      if (persistedState.entities) setEntities(persistedState.entities)
      if (persistedState.userLocation) setUserLocation(persistedState.userLocation)
      if (persistedState.radius) setRadius(persistedState.radius)
      if (persistedState.searchLocation) setSearchLocation(persistedState.searchLocation)
      if (persistedState.searchLocationName) setSearchLocationName(persistedState.searchLocationName)
    }
  }, [mode])
  
  // Actions
  const updateEntities = useCallback((newEntities) => {
    setEntities(newEntities)
  }, [])
  
  const selectEntity = useCallback((entityId) => {
    setSelectedEntityId(entityId)
    setFocusedEntityId(entityId)
  }, [])
  
  const clearSelection = useCallback(() => {
    setSelectedEntityId(null)
    setFocusedEntityId(null)
    setIsolatedEntityId(null)
  }, [])
  
  const updateUserLocation = useCallback((location) => {
    setUserLocation(location)
  }, [])
  
  const toggleUserLocation = useCallback(() => {
    setShowUserLocation(prev => {
      const newValue = !prev
      storage.set('showUserLocation', newValue)
      return newValue
    })
  }, [])
  
  const resetMapState = useCallback(() => {
    setEntities([])
    setUserLocation(null)
    setRadius(50)
    setSearchLocation('')
    setSearchLocationName('')
    clearSelection()
    setIsLoading(false)
    setError(null)
  }, [clearSelection])
  
  const value = {
    // State
    entities,
    userLocation,
    showUserLocation,
    radius,
    searchLocation,
    searchLocationName,
    mapRef,
    markerRefs,
    selectedEntityId,
    focusedEntityId,
    isolatedEntityId,
    isLoading,
    error,
    mode,
    
    // Setters (direct)
    setEntities: updateEntities,
    setUserLocation: updateUserLocation,
    setShowUserLocation: toggleUserLocation,
    setRadius,
    setSearchLocation,
    setSearchLocationName,
    setMapRef,
    setMarkerRefs,
    setFocusedEntityId,
    setIsolatedEntityId,
    setIsLoading,
    setError,
    
    // Actions (composite)
    selectEntity,
    clearSelection,
    resetMapState
  }
  
  return <MapContext.Provider value={value}>{children}</MapContext.Provider>
}

/**
 * useMapContext - Access map context
 * Must be used within MapProvider
 */
export function useMapContext() {
  const context = useContext(MapContext)
  if (!context) {
    throw new Error('useMapContext must be used within MapProvider')
  }
  return context
}

export default MapContext
